<template>
	<div
		class="settlement-detail"
		:class="{
			'settlement-detail--upgrade': selectedTabIndex === 0,
			'settlement-detail--stats': selectedTabIndex === 1,
			'settlement-detail--collapsed': isSettlementWindowCollapsed,
		}"
	>
		<div
			v-if="!isConnected"
			class="out-of-order-section"
			:class="{ 'switch-showed': showArrowsToSettlementSwitch }"
		>
			<span>{{ translate("ui.connect_road_town") }}</span>
		</div>
		<Tabs v-else :tabs="settlementTabs" @tabSelected="handleTabSelect">
			<template v-slot:tab1>
				<UpgradeSettlementTileDetailView
					@isAvailableUpgrade="$emit('isAvailableUpgrade', $event)"
				/>
			</template>
			<template v-slot:tab2>
				<StatsSettlementTileDetailView />
			</template>
		</Tabs>
		<div
			class="switch-settlement-buttons"
			v-if="showArrowsToSettlementSwitch && selectedTabIndex === 0"
		>
			<div
				class="switch-settlement-button"
				@click="switchSettlement('previous')"
			>
				<div class="switch-arrow switch-arrow--left arrow--left-22" />
			</div>
			<div
				class="switch-settlement-button"
				@click="switchSettlement('next')"
			>
				<div class="switch-arrow switch-arrow--right arrow--right-22" />
			</div>
		</div>
	</div>
</template>

<script lang="ts" setup>
import Tabs from "@/components/Tabs.vue";
import type SettlementCastleBuildingTileEntity from "@/game/entities/buildings/SettlementCastleBuildingTileEntity";
import { settlementNames } from "@/game/entities/config/settlements";
import useEmitter, { EmitterEvents } from "@/hooks/useEmitter";
import useTileRepository from "@/hooks/useTileRepository";
import useRuntimeStore from "@/store/useRuntimeStore";
import useSettingsStore from "@/store/useSettingsStore";
import type { SettlementItem } from "@/types/tile";
import { translate } from "@/utils/translate";
import StatsSettlementTileDetailView from "@/views/TileDetail/SubTabs/StatsSettlementTileDetailView.vue";
import UpgradeSettlementTileDetailView from "@/views/TileDetail/SubTabs/UpgradeSettlementTileDetailView.vue";
import { storeToRefs } from "pinia";
import { computed, markRaw, ref } from "vue";

interface Props {
	isConnected?: boolean;
	tileIndex?: number | null;
}

const switchBtnEnabled = ref(true);

const tilerepo = markRaw(useTileRepository());

const { isConnected = false, tileIndex = null } = defineProps<Props>();

const emitter = markRaw(useEmitter());

const settlementTabs = [{ name: "ui.upgrade" }, { name: "ui.stats" }];
const selectedTabIndex = ref<number>(0);

const runtimeStore = useRuntimeStore();
const { isSettlementWindowCollapsed } = storeToRefs(runtimeStore);

const settingsStore = useSettingsStore();
const { showArrowsToSettlementSwitch } = storeToRefs(settingsStore);

const castleEntities = computed(
	(): Array<SettlementCastleBuildingTileEntity> => {
		if (!showArrowsToSettlementSwitch.value) return [];

		return Array.from(settlementNames).reduce(
			(
				acc: Array<SettlementCastleBuildingTileEntity>,
				[, { x, y }]: [number, SettlementItem],
			): Array<SettlementCastleBuildingTileEntity> => {
				const index = tilerepo.getTileIndexByTileCoordinates({
					x,
					y,
				});
				const entity = tilerepo.getTileEntityByTileIndex(
					index,
				) as SettlementCastleBuildingTileEntity;

				if (tileIndex !== null) {
					acc.sort(
						(
							a: SettlementCastleBuildingTileEntity,
							b: SettlementCastleBuildingTileEntity,
						) => a.distance - b.distance,
					);
				}
				if (entity?.isRevealed) acc.push(entity);
				return acc;
			},
			new Array<SettlementCastleBuildingTileEntity>(),
		);
	},
);

const handleTabSelect = (tabIndex: number): void => {
	selectedTabIndex.value = tabIndex;
};

const switchSettlement = (direction: "previous" | "next"): void => {
	if (!tileIndex || !switchBtnEnabled.value) return;

	let currentSettlement = castleEntities.value.find(
		(entity: SettlementCastleBuildingTileEntity): boolean =>
			entity.index === tileIndex,
	);

	const currentPos = castleEntities.value.findIndex(
		(item: SettlementCastleBuildingTileEntity): boolean =>
			item.index === tileIndex,
	);

	const step = direction === "next" ? 1 : -1;

	const nextPos =
		(currentPos + step + castleEntities.value.length) %
		castleEntities.value.length;

	currentSettlement = castleEntities.value[nextPos];
	emitter.emit(EmitterEvents.PhaserSettlementSwitch, currentSettlement);
	switchBtnEnabled.value = false;
};
</script>

<style lang="scss" scoped>
.settlement-detail {
	display: flex;

	&.settlement-detail--collapsed {
		display: none;
	}

	.out-of-order-section {
		@include flex-col-center;
		position: relative;
		margin: 0 auto;
		margin-bottom: 1rem;
		box-shadow: inset 0 0 0.75rem 0 rgba(29, 30, 33, 0.8);
		border: solid 0.063rem #cf0032;
		padding: 0.5rem 1rem;
		width: 95%;
		min-height: 4rem;
		font-weight: bold;
		text-align: center;
		&::before {
			position: absolute;
			top: 0;
			left: 0;
			opacity: 1 !important;
			// NOTE: Multiply blending is better for light colors
			mix-blend-mode: multiply !important;
			background: url($path-images + "backgrounds/textures/marble-texture-bg.avif")
				repeat left;
			background-size: 80%;
			width: 100%;
			height: 100%;
			content: "";
		}

		& > span {
			color: #cf0032;
			font-size: 0.906rem;
			text-shadow: 0.044rem 0.044rem 0.063rem rgba(0, 0, 0, 0.6);
			text-transform: uppercase;
		}
		&.switch-showed {
			width: 70%;
		}
	}
	.switch-settlement-buttons {
		display: flex;
		position: absolute;
		bottom: 0.7rem;
		justify-content: space-between;
		align-items: center;
		padding: 0 0.5rem;
		width: 100%;
		pointer-events: none;
		.switch-settlement-button {
			display: flex;
			position: relative;
			justify-content: center;
			align-items: center;
			cursor: pointer;
			box-shadow: inset 0 0 0.5rem 0 rgba(0, 0, 0, 0.3);
			border-width: 0.075rem;
			border-style: solid;
			border-image-source: linear-gradient(to top, #bd9032, #f0d386);
			border-image-slice: 1;
			background-image: linear-gradient(to top, #181a1d, #303239);
			width: 2.5rem;
			height: 2.5rem;
			pointer-events: all;
		}
	}
}
</style>
